import { computed, ref, readonly } from 'vue';
import { defineStore } from 'pinia';
import { Config_Mode } from '@/types/graphql';
import type {
  DeleteConfigModulDto,
  DeleteConnectionMutation,
  DeleteConnectionMutationVariables,
  GenerateConfigEditSessionMutation,
  GenerateConfigEditSessionMutationVariables,
  GetConfigFileQuery,
  GetConfigFileQueryVariables,
  GetConfigFileStructureQuery,
  GetConfigFileStructureQueryVariables,
  LocalizedConfigBlockDto,
  LocalizedElementDto,
  LockConfigMutation,
  LockConfigMutationVariables,
  HeartbeatLockMutation,
  HeartbeatLockMutationVariables,
  UnlockConfigMutation,
  UnlockConfigMutationVariables,
  UpdateConfigValuesMutation,
  UpdateConfigValuesMutationVariables,
} from '@/types/graphql';
import getConfigFileQuery from '@/graphql/queries/getConfigFile.graphql';
import getConfigFileStructureQuery from '@/graphql/queries/getConfigFileStructure.graphql';
import canAcquireConfigLockQuery from '@/graphql/queries/canAcquireConfigLock.graphql';
import updateConfigValuesMutation from '@/graphql/mutations/editConfigFile.graphql';
import deleteConnectionMutation from '@/graphql/mutations/deleteConnection.graphql';
import generateConfigEditSessionMutation from '@/graphql/mutations/generateConfigEditSession.graphql';
import lockConfigMutation from '@/graphql/mutations/lockConfig.graphql';
import heartbeatLockMutation from '@/graphql/mutations/heartbeatLock.graphql';
import unlockConfigMutation from '@/graphql/mutations/unlockConfig.graphql';
import { buildElementLookupMap } from '~/Common/ConfigHelpers';
import storeGraphqlQuery from '~/composables/storeGraphqlQuery';
import storeGraphqlMutation from '~/composables/storeGraphqlMutation';

type CanAcquireConfigLockQuery = {
  canAcquireConfigLock: boolean;
};

type CanAcquireConfigLockQueryVariables = {
  configId: number;
};

export const useConfigStore = defineStore("config", () => {
  // State
  const configFileId = ref<number | null>(null);
  const configStructureFileId = ref<number | null>(null);
  const configFileStructure = ref<GetConfigFileStructureQuery['getConfigFileStructure'] | null>(null);
  const loading = ref(false);
  const currentLocale = ref<string>(''); // Track the locale used to load current structure
  const editSessionToken = ref<string | null>(null);
  const editSessionConfigId = ref<number | null>(null);
  const heartbeatTimer = ref<ReturnType<typeof setInterval> | null>(null);
  const savingConfigIds = ref(new Set<number>());
  const recentlyUpdatedConfigIds = ref(new Set<number>());
  const recentlyUpdatedTimers = new Map<number, ReturnType<typeof setTimeout>>();
  const RECENT_CONFIG_UPDATE_MS = 35_000;

  // Computed properties
  const isLoading = computed(() => loading.value);
  const isEditSessionActive = computed(() =>
    !!editSessionToken.value && !!editSessionConfigId.value,
  );

  // New lookup map for fast element access
  const elementLookupMap = ref<Map<number, { element: LocalizedElementDto; moduleInfo: LocalizedConfigBlockDto }>>(new Map());

  // Actions
  function stopHeartbeat() {
    if (heartbeatTimer.value) {
      clearInterval(heartbeatTimer.value);
      heartbeatTimer.value = null;
    }
  }

  function isConfigSavePending(configId?: number | null): boolean {
    if (!configId) return false;
    return savingConfigIds.value.has(configId);
  }

  function markConfigRecentlyUpdated(configId: number) {
    recentlyUpdatedConfigIds.value.add(configId);

    const existingTimer = recentlyUpdatedTimers.get(configId);

    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      recentlyUpdatedConfigIds.value.delete(configId);
      recentlyUpdatedTimers.delete(configId);
    }, RECENT_CONFIG_UPDATE_MS);

    recentlyUpdatedTimers.set(configId, timer);
  }
  function isConfigRecentlyUpdated(configId?: number | null): boolean {
    if (!configId) return false;
    return recentlyUpdatedConfigIds.value.has(configId);
  }


  function resetEditSessionState() {
    stopHeartbeat();
    editSessionToken.value = null;
    editSessionConfigId.value = null;
  }

  function startHeartbeat(configId: number, sessionToken: string) {
    stopHeartbeat();
    heartbeatTimer.value = setInterval(async () => {
      try {
        await storeGraphqlMutation<
          HeartbeatLockMutation,
          HeartbeatLockMutationVariables
        >(
          {
            mutation: heartbeatLockMutation,
            variables: { configId, sessionToken },
          },
          'Error while extending config lock'
        );
      } catch (error) {
        console.error(error);
        stopHeartbeat();
      }
    }, 30_000);
  }

   async function openEditSession(configId?: number): Promise<boolean> {
     const targetConfigId = configId ?? configFileId.value;
     if (!targetConfigId) return false;

     if (
       editSessionToken.value &&
       editSessionConfigId.value === targetConfigId
     ) {
       return true;
     }

     if (editSessionToken.value && editSessionConfigId.value) {
       await releaseEditSession();
     }

     const sessionRes = await storeGraphqlMutation<
       GenerateConfigEditSessionMutation,
       GenerateConfigEditSessionMutationVariables
     >(
       {
         mutation: generateConfigEditSessionMutation,
         variables: { configId: targetConfigId },
       },
       'Error while creating config edit session'
     );

     const sessionToken = sessionRes?.data?.generateConfigEditSession?.sessionToken;
     if (!sessionToken) return false;

     try {
       await storeGraphqlMutation<LockConfigMutation, LockConfigMutationVariables>(
         {
           mutation: lockConfigMutation,
           variables: { configId: targetConfigId, sessionToken },
         },
         'Error while acquiring config lock',
         false // Don't show auto-toast - lock conflicts are handled by caller
       );

       editSessionConfigId.value = targetConfigId;
       editSessionToken.value = sessionToken;
       startHeartbeat(targetConfigId, sessionToken);
       return true;
     } catch (error) {
       // Lock acquisition failed, clean up the session
       await storeGraphqlMutation<
         UnlockConfigMutation,
         UnlockConfigMutationVariables
       >(
         {
           mutation: unlockConfigMutation,
           variables: { configId: targetConfigId, sessionToken },
         },
         'Error while cleaning up session',
         false // Don't show auto-toast for cleanup
       ).catch(() => {
         // Ignore cleanup errors
       });
       throw error;
     }
   }

  async function releaseEditSession(): Promise<void> {
    if (!editSessionToken.value || !editSessionConfigId.value) {
      resetEditSessionState();
      return;
    }

    const sessionToken = editSessionToken.value;
    const configId = editSessionConfigId.value;
    resetEditSessionState();

    try {
      await storeGraphqlMutation<
        UnlockConfigMutation,
        UnlockConfigMutationVariables
      >(
        {
          mutation: unlockConfigMutation,
          variables: { configId, sessionToken },
        },
        'Error while releasing config lock'
      );
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Fetches the configuration file by its ID.
   * @param id - The ID of the configuration file to fetch.
   * @return A promise that resolves to the configuration file data or null if not found.
   */
  async function getConfigFile(
    id: number,
    configMode: Config_Mode = Config_Mode.Production,
  ): Promise<GetConfigFileQuery | null> {
    loading.value = true;

    try {
      const res = await storeGraphqlQuery<
        GetConfigFileQuery,
        GetConfigFileQueryVariables
      >(
        {
          query: getConfigFileQuery,
          variables: {
            id,
            configMode,
          } as any,
        },
        'Error while loading configuration file'
      );

      if (res?.data) {
        configFileId.value = res.data.getConfigFile.configId!;
        configStructureFileId.value = res.data.getConfigFile.structureId!;
        return res.data;
      }
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetches the structure of a configuration file by its ID and language code.
   * @param configStructureFileId - The ID of the configuration structure file to fetch.
   * @param languageCode - The language code to use for translations.
   * @return A promise that resolves to the configuration file structure or null if not found.
   */
  async function getConfigFileStructure(configStructureFileId: number, languageCode: string): Promise<GetConfigFileStructureQuery | null> {
    loading.value = true;

    try {
      const res = await storeGraphqlQuery<
        GetConfigFileStructureQuery,
        GetConfigFileStructureQueryVariables
      >(
        {
          query: getConfigFileStructureQuery,
          variables: { configStructureFileId, languageCode },
        },
        'Error while loading configuration file structure'
      );

      if (res?.data) {
        configFileStructure.value = res.data.getConfigFileStructure;
        currentLocale.value = languageCode; // Track which locale was used for this structure
        // Build lookup map after loading structure
        elementLookupMap.value = buildElementLookupMap(configFileStructure.value);
      }
      return res?.data ?? null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Updates configuration values based on the provided changes.
   * @param changes - An array of changes where each change contains an ID and the new element value.
   * @param languageCode - The language code to use for translations.
   * @return A promise that resolves to the updated configuration values or null if the update fails.
   */
  async function updateConfigValues(
    changes: { id: number; elementValue: string }[],
    languageCode: string
  ) {
    if (!configFileId.value) {
      return null;
    }

    const targetConfigId = configFileId.value;

    savingConfigIds.value.add(targetConfigId);
    loading.value = true;

    try {
      const isSessionOpen = await openEditSession(configFileId.value);
      if (!isSessionOpen || !editSessionToken.value) {
        return null;
      }

      const res = await storeGraphqlMutation<
        UpdateConfigValuesMutation,
        UpdateConfigValuesMutationVariables & { sessionToken: string }
      >(
        {
          mutation: updateConfigValuesMutation,
          variables: {
            changes,
            sessionToken: editSessionToken.value,
          },
        },
        'Error while updating configuration values'
      );

      if (res?.data && configFileId.value) {
        markConfigRecentlyUpdated(targetConfigId);
        await getConfigFileStructure(configStructureFileId.value!, languageCode);
      }
      return res?.data ?? null;
    } catch (error) {
      if (configFileId.value) {
        await getConfigFileStructure(configStructureFileId.value!, languageCode);
      }
      throw error;
    } finally {
      savingConfigIds.value.delete(targetConfigId);
      loading.value = false;
    }
  }

  /**
   * Deletes a connection based on the provided XPath and language code.
   * @param xpath - The XPath of the connection to delete.
   * @param languageCode - The language code to use for translations.
   * @return A promise that resolves to the result of the deletion or null if the deletion fails.
   */
   async function deleteConnection(xpath: string, languageCode: string) {
     loading.value = true;
     try {
         const input = {
         xpath,
           configFileId: configFileId.value!,
         } as DeleteConfigModulDto;

         const res = await storeGraphqlMutation<
           DeleteConnectionMutation,
           DeleteConnectionMutationVariables
         >(
           {
             mutation: deleteConnectionMutation,
             variables: {
               input,
             },
           },
           "Error while deleting connection"
         );

       if (res?.data && configFileId.value) {
         await getConfigFileStructure(configStructureFileId.value!, languageCode);
       }
       return res?.data ?? null;
     } catch (error) {
       if (configStructureFileId.value) {
         await getConfigFileStructure(configStructureFileId.value, languageCode);
       }
       throw error;
     } finally {
       loading.value = false;
     }
   }

   /**
    * Checks whether the current user can acquire the config lock.
    * This call is read-only and does not try to lock/unlock.
    */
   async function checkConfigLockStatus(configId: number): Promise<boolean> {
     const res = await storeGraphqlQuery<
       CanAcquireConfigLockQuery,
       CanAcquireConfigLockQueryVariables
     >(
       {
         query: canAcquireConfigLockQuery,
         variables: { configId },
       },
       'Error while checking config lock status',
     );

     return !!res?.data?.canAcquireConfigLock;
   }

   // Return state, getters, and actions
   return {
     configFileId,
     configStructureFileId,
     configFileStructure,
     currentLocale: readonly(currentLocale),
     loading,
     isLoading,
     getConfigFile,
     getConfigFileStructure,
     updateConfigValues,
     isConfigRecentlyUpdated,
     isConfigSavePending,
     openEditSession,
     releaseEditSession,
     isEditSessionActive,
     deleteConnection,
     checkConfigLockStatus,
     getElementLookupMap: readonly(elementLookupMap),
   };
});